import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {loadApplication, resendCoApplicantInvite, deleteCoApplicantInvite} from '../../api'
import {CoApplicantInviteSuccess} from '../common/CoApplicantInviteModal'
import CoApplicantInviteModal from '../common/CoApplicantInviteModal'
import {normalizeAustralianMobile} from '../common/applicationPersonUtils'
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import {IApplicationFormData, IWizardCoApplicant} from '../../models/application-form'
import {Modal} from 'react-bootstrap'
import {isDeclinedInviteStatus, isInviteExpired} from '../renter-home/renterHomeUtils'

interface HouseHoldStepProps {
  register: UseFormRegister<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  trigger: UseFormTrigger<IApplicationFormData>
}

const HouseHoldStep: React.FC<HouseHoldStepProps> = ({errors, setValue, watch, trigger}) => {
  const ownermobileNumber = watch('mobileNumber')
  const applicationType = watch('applicationType')
  const watchedCoApplicants = watch('coApplicants')
  const coApplicants = useMemo(() => watchedCoApplicants ?? [], [watchedCoApplicants])
  const sessionId = watch('sessionId')
  const blockNotVerifiedCoApplicant = watch('blockNotVerifiedCoApplicant')
  const coApplicantsError = errors?.coApplicants as {message?: string} | undefined
  const [showInviteModal, setShowInviteModal] = useState(coApplicants.length === 0)
  const [inviteMessage, setInviteMessage] = useState<string | null>(null)
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null)
  const [deletingInviteId, setDeletingInviteId] = useState<string | null>(null)
  const [inviteToDelete, setInviteToDelete] = useState<IWizardCoApplicant | null>(null)
  const [isRefreshingStatuses, setIsRefreshingStatuses] = useState(false)
  const [inviteCooldownById, setInviteCooldownById] = useState<Record<string, number>>({})

  const getInviteCooldown = (inviteId?: string): number =>
    inviteId ? inviteCooldownById[inviteId] ?? 0 : 0

  useEffect(() => {
    if (coApplicants.length === 0) {
      setShowInviteModal(true)
    } else {
      trigger()
    }
  }, [coApplicants.length, trigger])

  useEffect(() => {
    const hasCooldowns = Object.values(inviteCooldownById).some((seconds) => seconds > 0)
    if (!hasCooldowns) {
      return
    }

    const timer = window.setInterval(() => {
      setInviteCooldownById((current) => {
        const next: Record<string, number> = {}
        let activeCount = 0

        Object.entries(current).forEach(([inviteId, seconds]) => {
          const nextSeconds = Math.max(0, seconds - 1)
          if (nextSeconds > 0) {
            next[inviteId] = nextSeconds
            activeCount += 1
          }
        })

        return activeCount > 0 ? next : {}
      })
    }, 1000)

    return () => window.clearInterval(timer)
  }, [inviteCooldownById])

  const updateCoApplicant = (incoming?: IWizardCoApplicant) => {
    if (!incoming?.mobileNumber) {
      return
    }

    const incomingInviteId = incoming.inviteId?.trim()
    const normalizedIncomingMobile = normalizeAustralianMobile(incoming.mobileNumber ?? '')
    const existingIndex = coApplicants.findIndex((entry) => {
      const entryInviteId = entry.inviteId?.trim()
      if (incomingInviteId && entryInviteId && incomingInviteId === entryInviteId) {
        return true
      }

      return normalizeAustralianMobile(entry.mobileNumber ?? '') === normalizedIncomingMobile
    })

    const nextList =
      existingIndex >= 0
        ? coApplicants.map((entry, index) =>
            index === existingIndex ? {...entry, ...incoming} : entry
          )
        : [...coApplicants, incoming]

    setValue('coApplicants', nextList, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const handleInviteSent = ({invite, retryAfterSeconds}: CoApplicantInviteSuccess) => {
    updateCoApplicant(invite)

    const inviteId = invite.inviteId?.trim()
    if (inviteId && retryAfterSeconds > 0) {
      setInviteCooldownById((current) => ({
        ...current,
        [inviteId]: Math.max(current[inviteId] ?? 0, retryAfterSeconds),
      }))
    }

    setInviteMessage('Invite sent.')
  }

  const resendInvite = async (entry: IWizardCoApplicant) => {
    const inviteId = entry.inviteId?.trim()
    if (!sessionId || !inviteId) {
      setInviteMessage('Invite reference is missing. Refresh and try again.')
      return
    }

    if (getInviteCooldown(inviteId) > 0 || resendingInviteId === inviteId) {
      return
    }

    setInviteMessage(null)
    setResendingInviteId(inviteId)
    try {
      const response = await resendCoApplicantInvite({
        sessionId,
        inviteId,
      })

      if (response.invite) {
        updateCoApplicant({
          inviteId: response.invite.inviteId,
          fullName: response.invite.fullName,
          mobileNumber: normalizeAustralianMobile(response.invite.mobileNumber),
          email: response.invite.email,
          role: response.invite.role,
          status: response.invite.status,
          isMobileVerified: response.invite.isMobileVerified,
          invitedAtUtc: response.invite.invitedAtUtc,
          inviteExpiresAtUtc: response.invite.inviteExpiresAtUtc,
          verifiedAtUtc: response.invite.verifiedAtUtc,
          revokedAtUtc: response.invite.revokedAtUtc,
          declinedAtUtc: response.invite.declinedAtUtc,
          declineReason: response.invite.declineReason,
        })
      }

      const retryAfterSeconds = response.retryAfterSeconds ?? 30
      if (retryAfterSeconds > 0) {
        const currentInviteId = response.invite?.inviteId?.trim() || inviteId
        setInviteCooldownById((current) => ({
          ...current,
          [currentInviteId]: Math.max(current[currentInviteId] ?? 0, retryAfterSeconds),
        }))
      }

      setInviteMessage(
        response.success
          ? response.message
            ? `Invite resent.`
            : 'Invite resent.'
          : response.message ?? 'Unable to resend invite.'
      )
    } catch {
      setInviteMessage('Unable to resend invite right now.')
    } finally {
      setResendingInviteId(null)
    }
  }

  const deleteInvite = async (entry: IWizardCoApplicant) => {
    const inviteId = entry.inviteId?.trim()
    if (!sessionId || !inviteId) {
      setInviteMessage('Invite reference is missing. Refresh and try again.')
      return
    }

    if (deletingInviteId === inviteId) {
      return
    }

    setInviteMessage(null)
    setDeletingInviteId(inviteId)
    try {
      const response = await deleteCoApplicantInvite({
        sessionId,
        inviteId,
      })

      if (!response.success) {
        setInviteMessage(response.message ?? 'Unable to delete invite right now.')
        return
      }

      const remainingCoApplicants = coApplicants.filter(
        (coApplicant) => coApplicant.inviteId?.trim() !== inviteId
      )
      setValue('coApplicants', remainingCoApplicants, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })

      setInviteCooldownById((current) => {
        const next = {...current}
        delete next[inviteId]
        return next
      })
      setInviteToDelete(null)
      setInviteMessage(response.message ?? 'Invite deleted.')
    } catch {
      setInviteMessage('Unable to delete invite right now.')
    } finally {
      setDeletingInviteId(null)
    }
  }

  const refreshInviteStatuses = useCallback(
    async ({silent = false}: {silent?: boolean} = {}) => {
      const normalizedSessionId = sessionId?.trim()
      if (!normalizedSessionId) {
        if (!silent) {
          setInviteMessage('Session expired. Please verify your mobile again.')
        }
        return
      }

      if (isRefreshingStatuses) {
        return
      }

      if (!silent) {
        setInviteMessage(null)
      }

      setIsRefreshingStatuses(true)
      try {
        const loaded = await loadApplication(normalizedSessionId)
        const loadedSessionId = loaded.sessionId?.trim()
        if (!loadedSessionId || loadedSessionId !== normalizedSessionId) {
          if (!silent) {
            setInviteMessage('Unable to refresh invite status right now.')
          }
          return
        }

        const refreshedCoApplicants = (loaded.coApplicants ?? []).map((entry) => ({
          ...entry,
          mobileNumber: normalizeAustralianMobile(entry.mobileNumber ?? ''),
        }))

        setValue('coApplicants', refreshedCoApplicants, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        })

        await trigger('coApplicants')
        if (!silent) {
          setInviteMessage('Invite status refreshed.')
        }
      } catch {
        if (!silent) {
          setInviteMessage('Unable to refresh invite status right now.')
        }
      } finally {
        setIsRefreshingStatuses(false)
      }
    },
    [isRefreshingStatuses, sessionId, setValue, trigger]
  )

  const openInviteModal = () => {
    setInviteMessage(null)
    setShowInviteModal(true)
  }

  const closeInviteModal = () => {
    setShowInviteModal(false)
  }

  useEffect(() => {
    const normalizedSessionId = sessionId?.trim()
    const hasPendingInvites = coApplicants.some(
      (entry) => entry.status === 'pending' && !entry.isMobileVerified
    )

    if (!normalizedSessionId || !hasPendingInvites) {
      return
    }

    const timer = window.setInterval(() => {
      void refreshInviteStatuses({silent: true})
    }, 30000)

    return () => window.clearInterval(timer)
  }, [coApplicants, refreshInviteStatuses, sessionId])

  function formatPhone(number?: string) {
    if (!number) return ''

    let digits = number.replace(/\D/g, '')

    digits = digits.slice(0, 10)

    if (digits.length < 5) return digits
    if (digits.length < 8) {
      return digits.replace(/(\d{4})(\d+)/, '$1 $2')
    }

    return digits.replace(/(\d{4})(\d{3})(\d+)/, '$1 $2 $3')
  }

  const getTitle = () => {
    switch (applicationType) {
      case 'Couple':
        return 'Invite your partner'

      case 'Housemates':
        return 'Invite your housemates'

      case 'Family':
        return 'Add a partner (optional)'

      default:
        return 'Who else is on the application?'
    }
  }

  const getSubTitle = () => {
    switch (applicationType) {
      case 'Couple':
        return "We'll text them a code to fill in their part."

      case 'Housemates':
        return "We'll text each person a code to fill in their part."

      case 'Family':
        return "If they're on the lease and applying with you, invite them — both incomes will count."

      default:
        return "We'll text each person a code to fill in their part."
    }
  }

  return (
    <div>
      <div className='text-center mb-2'>
        <h2 className='wizard-auth-title'>{getTitle()}</h2>
        <p className='text-muted fs-4'>{getSubTitle()}</p>
      </div>

      {coApplicants.length > 0 && (
        <>
          <div className='d-flex justify-content-end'>
            <button
              type='button'
              className='btn btn-xs btn-light'
              onClick={() => {
                void refreshInviteStatuses()
              }}
              disabled={isRefreshingStatuses || !sessionId}
            >
              <i className='bi bi-arrow-clockwise' style={{margin: 0, padding: 0}}></i>
            </button>
          </div>

          <div className='d-grid gap-2 mb-4'>
            {coApplicants.map((entry) => {
              const status = entry.status?.trim().toLowerCase()
              const isDeclined = isDeclinedInviteStatus(
                status,
                entry.declinedAtUtc,
                entry.declineReason
              )
              const isExpired =
                status === 'expired' ||
                (!isDeclined &&
                  entry.isMobileVerified !== true &&
                  isInviteExpired(entry.inviteExpiresAtUtc))
              const isRevoked = status === 'revoked'
              const statusClass = isRevoked
                ? 'status-revoked'
                : isDeclined
                ? 'status-declined'
                : isExpired
                ? 'status-expired'
                : entry.isMobileVerified
                ? 'status-verified'
                : 'pill-pending'

              const statusLabel = isRevoked
                ? 'Revoked'
                : isDeclined
                ? "This Isn't Me"
                : isExpired
                ? 'Expired'
                : entry.isMobileVerified
                ? 'Accepted'
                : 'Pending'

              return (
                <div key={entry.inviteId ?? entry.mobileNumber} className='wizard-coapp-card'>
                  <div className='row align-items-center'>
                    {/* Name + Mobile */}
                    <div className='col'>
                      <div className='fw-semibold'>{entry.fullName || 'Co-applicant'}</div>
                      <div className='text-muted fs-4'>{formatPhone(entry.mobileNumber!)}</div>
                      {isDeclined && (
                        <div className='text-danger small mt-2'>
                          They selected "This isn't me". The contact details may be wrong.
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className='col-auto'>
                      <div className={`pill ${statusClass}`}>
                        <svg
                          width='12'
                          height='12'
                          viewBox='0 0 20 20'
                          fill='none'
                          stroke='#B4530A'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <path d='M15.5 10A5.5 5.5 0 115 6.5M14 4v3h-3'></path>
                        </svg>
                        <span>{statusLabel}</span>
                      </div>
                      {/* Actions */}
                      <div className='d-flex gap-2'>
                        {false && entry.status === 'pending' && (
                          <button
                            type='button'
                            className='btn btn-xs btn-light p-4 mt-2'
                            onClick={() => resendInvite(entry)}
                            disabled={
                              !entry.inviteId ||
                              resendingInviteId === entry.inviteId ||
                              getInviteCooldown(entry.inviteId) > 0
                            }
                          >
                            <svg
                              width='16'
                              height='16'
                              viewBox='0 0 20 20'
                              fill='#cccccc'
                              stroke='#bbbbbb'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            >
                              <path d='M17 3L2 9l6 2 2 6 7-14z' />
                              <path d='M8 11l4-4' />
                            </svg>
                            {resendingInviteId === entry.inviteId
                              ? ' Resending...'
                              : ' Resend Invite'}{' '}
                            {entry.inviteId && getInviteCooldown(entry.inviteId) > 0 && (
                              <span className='small text-muted'>
                                {getInviteCooldown(entry.inviteId)}s
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Delete */}
                    <div className='col-auto'>
                      {entry.status !== 'revoked' ? (
                        <button
                          type='button'
                          className='btn btn-xs btn-icon btn-light'
                          onClick={() => {
                            setInviteMessage(null)
                            setInviteToDelete(entry)
                          }}
                          disabled={!entry.inviteId || deletingInviteId === entry.inviteId}
                          aria-label='Delete co-applicant invite'
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 20 20'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.6'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          >
                            <path d='M5 5l10 10M15 5L5 15'></path>
                          </svg>
                        </button>
                      ) : (
                        <div style={{width: 45}}></div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {coApplicants.length > 0 || !showInviteModal ? (
        <button
          type='button'
          className='btn btn-light w-100 border mb-4'
          style={{background: '#ffffff'}}
          onClick={openInviteModal}
        >
          <i className='bi bi-plus-lg me-2'></i>
          Add another person
        </button>
      ) : null}

      {inviteMessage && (
        <div
          className={`small mt-3 ${
            /unable|required|expired|missing|right now/i.test(inviteMessage)
              ? 'text-danger'
              : 'text-success'
          }`}
        >
          {inviteMessage}
        </div>
      )}

      <CoApplicantInviteModal
        isOpen={showInviteModal}
        sessionId={sessionId}
        ownerMobileNumber={ownermobileNumber}
        existingCoApplicants={coApplicants}
        onClose={closeInviteModal}
        onInvited={handleInviteSent}
      />

      <Modal
        contentClassName='household-step-modal'
        show={Boolean(inviteToDelete)}
        onHide={() => {
          if (!deletingInviteId) {
            setInviteToDelete(null)
          }
        }}
        backdrop={deletingInviteId ? 'static' : true}
        keyboard={!deletingInviteId}
        centered
      >
        <Modal.Body>
          <div className=''>
            <div className='title'>Remove {inviteToDelete?.fullName || 'this co-applicant'}?</div>
            <div className='description'>
              They'll lose access to this application. You can re-invite them anytime.
            </div>
            <div className='footer'>
              <button
                type='button'
                className='cancel'
                disabled={Boolean(deletingInviteId)}
                onClick={() => setInviteToDelete(null)}
              >
                Cancel
              </button>
              <button
                type='button'
                className='remove'
                disabled={Boolean(deletingInviteId) || !inviteToDelete}
                onClick={() => {
                  if (inviteToDelete) {
                    void deleteInvite(inviteToDelete)
                  }
                }}
              >
                {deletingInviteId ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
          {inviteMessage && /unable|required|expired|missing|right now/i.test(inviteMessage) && (
            <div className='text-danger small mt-3' role='alert'>
              {inviteMessage}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {coApplicants.length > 0 && !blockNotVerifiedCoApplicant && (
        <div className='wizard-hint-card mb-4 d-flex ' style={{padding: '20px'}}>
          <i className='bi bi-info-circle me-5 gradient-icon'></i>
          <span>
            Keep going - they'll show as "Pending" until they verify. You don't need to wait.
          </span>
        </div>
      )}

      {coApplicantsError?.message && (
        <div className='text-danger mt-3'>{String(coApplicantsError.message)}</div>
      )}
    </div>
  )
}

export default HouseHoldStep
